import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, AlertTriangle, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

/**
 * Two-click unsubscribe flow.
 *
 * The email link lands users on a confirmation page; the destructive API call
 * only fires after the user clicks "Confirm unsubscribe". This is the pattern
 * Gmail/Outlook recommend so that anti-virus and link-prefetchers can't
 * accidentally unsubscribe people, and it survives React StrictMode's
 * dev-mode double-mount because no fetch happens on render.
 */
const Unsubscribe = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('idle'); // idle | submitting | unsubscribed | already_off | error
  const [email, setEmail] = useState('');

  const handleConfirm = async () => {
    if (!token || state === 'submitting' || state === 'unsubscribed') return;
    setState('submitting');
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/unsubscribe/${encodeURIComponent(token)}`;
      const res = await axios.get(url);
      const data = res?.data;
      if (data?.status === 'unsubscribed') {
        setEmail(data.email || '');
        setState('unsubscribed');
      } else {
        setState('already_off');
      }
    } catch {
      setState('error');
    }
  };

  return (
    <div className="bg-[#FDFEFA] min-h-screen flex items-center" data-testid="unsubscribe-page">
      <div className="max-w-[680px] mx-auto px-6 md:px-12 py-24 w-full">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white border border-[#242326]/8 rounded-3xl p-8 md:p-12 shadow-sm"
        >
          {state === 'idle' && (
            <div data-testid="unsubscribe-confirm">
              <Mail className="w-10 h-10 text-[#83BF42] mb-4" />
              <h1 className="text-[#242326] text-3xl md:text-4xl font-light font-['Inter'] leading-tight mb-3">
                Unsubscribe from the Grouth newsletter?
              </h1>
              <p className="text-[#4A4A48] text-base font-['Lato'] font-light leading-relaxed">
                Click the button below to confirm. We&rsquo;ll stop sending you our weekly issues.
              </p>
              <p className="text-[#B8B8B8] text-xs font-['Lato'] mt-3">
                Why two clicks? Some inbox providers and anti-virus tools auto-open every link.
                We don&rsquo;t want to unsubscribe you by accident.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  onClick={handleConfirm}
                  data-testid="unsubscribe-confirm-button"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#242326] text-white text-sm font-['Inter'] hover:bg-[#1a1a1d] transition-all duration-300 group"
                >
                  Confirm unsubscribe
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => navigate('/insights')}
                  data-testid="unsubscribe-keep"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#242326]/15 text-[#242326] text-sm font-['Inter'] hover:bg-[#242326]/5 transition"
                >
                  Keep me subscribed
                </button>
              </div>
            </div>
          )}

          {state === 'submitting' && (
            <div className="text-center py-6" data-testid="unsubscribe-loading">
              <div className="w-7 h-7 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-[#4A4A48] text-sm font-['Lato'] mt-4">Updating your preferences…</p>
            </div>
          )}

          {state === 'unsubscribed' && (
            <div data-testid="unsubscribe-success">
              <CheckCircle2 className="w-10 h-10 text-[#83BF42] mb-4" />
              <h1 className="text-[#242326] text-3xl md:text-4xl font-light font-['Inter'] leading-tight mb-3">
                You&rsquo;re unsubscribed.
              </h1>
              <p className="text-[#4A4A48] text-base font-['Lato'] font-light leading-relaxed">
                {email
                  ? <>We&rsquo;ve removed <strong className="text-[#242326]">{email}</strong> from the Grouth newsletter. </>
                  : <>We&rsquo;ve removed your address from the Grouth newsletter. </>}
                You won&rsquo;t receive any more issues unless you re-subscribe.
              </p>
              <p className="text-[#B8B8B8] text-xs font-['Lato'] mt-3">
                Changed your mind? You can re-subscribe any time on our Insights page.
              </p>
            </div>
          )}

          {state === 'already_off' && (
            <div data-testid="unsubscribe-already-off">
              <AlertTriangle className="w-10 h-10 text-amber-500 mb-4" />
              <h1 className="text-[#242326] text-3xl md:text-4xl font-light font-['Inter'] leading-tight mb-3">
                You&rsquo;re already off the list.
              </h1>
              <p className="text-[#4A4A48] text-base font-['Lato'] font-light leading-relaxed">
                This unsubscribe link has already been used, or the address isn&rsquo;t in our records.
                Either way, you won&rsquo;t receive any more newsletter emails.
              </p>
            </div>
          )}

          {state === 'error' && (
            <div data-testid="unsubscribe-error">
              <AlertTriangle className="w-10 h-10 text-red-500 mb-4" />
              <h1 className="text-[#242326] text-3xl md:text-4xl font-light font-['Inter'] leading-tight mb-3">
                Something went wrong.
              </h1>
              <p className="text-[#4A4A48] text-base font-['Lato'] font-light leading-relaxed">
                We couldn&rsquo;t process this unsubscribe request right now. Please try again in a moment, or
                email <a href="mailto:hello@grouth.co" className="text-[#83BF42] underline">hello@grouth.co</a> and we&rsquo;ll remove you manually.
              </p>
              <button
                onClick={handleConfirm}
                data-testid="unsubscribe-retry"
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#242326] text-white text-sm font-['Inter'] hover:bg-[#1a1a1d]"
              >
                Try again
              </button>
            </div>
          )}

          {(state === 'unsubscribed' || state === 'already_off' || state === 'error') && (
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/')}
                data-testid="unsubscribe-home"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#242326] text-white text-sm font-['Inter'] hover:bg-[#1a1a1d] transition-all duration-300 group"
              >
                Back home
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/insights')}
                data-testid="unsubscribe-insights"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#242326]/15 text-[#242326] text-sm font-['Inter'] hover:bg-[#242326]/5 transition"
              >
                Read the latest
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Unsubscribe;
